import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import { useSeo } from "@/hooks/useSeo";
import { articles, faqs } from "@/mocks/articles";
import { site } from "@/mocks/site";
import HeroSection from "./components/HeroSection";
import TrendingTicker from "./components/TrendingTicker";
import StatsStrip from "./components/StatsStrip";
import RankingSection from "./components/RankingSection";
import CategoriesSection from "./components/CategoriesSection";
import LatestSection from "./components/LatestSection";
import DigestSection from "./components/DigestSection";
import FaqSection from "./components/FaqSection";
import SubscribeSection from "./components/SubscribeSection";

export default function Home() {
  const location = useLocation();

  const baseUrl = `${window.location.origin}${__BASE_PATH__.replace(/\/$/, "")}`;

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: site.name,
        alternateName: site.nameEn,
        url: baseUrl,
        email: site.email,
        description: site.intro,
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: site.name,
        url: baseUrl,
        inLanguage: "zh-CN",
        potentialAction: {
          "@type": "SearchAction",
          target: `${baseUrl}/?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "今日热瓜榜",
        itemListElement: [...articles]
          .sort((a, b) => b.heat - a.heat)
          .slice(0, 8)
          .map((article, index) => ({
            "@type": "ListItem",
            position: index + 1,
            url: `${baseUrl}/article/${article.slug}`,
            name: article.title,
          })),
      },
    ],
    [baseUrl],
  );

  useSeo({
    title: "瓜网 - 娱乐八卦与流行文化资讯门户 | 吃瓜热点每日更新",
    description:
      "瓜网是聚焦娱乐八卦与流行文化的资讯门户，每日整理明星动态、影视综艺、音乐现场、网红观察与时尚生活话题，提供热瓜榜、栏目精选与每周盘点，帮你快速看懂娱乐热点。",
    keywords: "瓜网,娱乐八卦,吃瓜资讯,明星动态,影视综艺,音乐现场,网红观察,热瓜榜",
    path: "/",
    image: articles[0].cover,
    jsonLd,
  });

  useEffect(() => {
    if (!location.hash) return undefined;
    const id = location.hash.replace("#", "");
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  return (
    <div id="top" className="min-h-screen bg-background-50">
      <SiteHeader brandAsHeading />
      <main>
        <HeroSection />
        <TrendingTicker />
        <StatsStrip />
        <RankingSection />
        <CategoriesSection />
        <LatestSection />
        <DigestSection />
        <FaqSection />
        <SubscribeSection />
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}